import { Lock, Sparkles } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { ToggleCard } from "~/components/ui/toggle-card";
import { useI18nContext } from "~/i18n/i18n-react";
import { findSttModel, getSttModelOptions } from "~/utils/stt-models";
import { MathDomainToggle } from "../../transcription/math-domain-toggle";
import { ModelSelect } from "../../transcription/model-select";
import { FormField } from "../../ui/form";
import type { CreateRoomFormValues } from "./create-room-schema";

interface RoomModelStepProps {
	form: UseFormReturn<CreateRoomFormValues>;
}

/**
 * Step two: the transcription model every participant inherits on join, and
 * whether they are allowed to change it.
 */
export const RoomModelStep = ({ form }: RoomModelStepProps) => {
	const { LL } = useI18nContext();

	const models = getSttModelOptions(LL);
	const selectedValue = form.watch("model");
	const selectedModel = findSttModel(models, selectedValue);

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-start gap-3">
				<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
					<Sparkles className="h-4 w-4 text-primary" />
				</div>
				<div className="flex flex-col gap-0.5">
					<p className="font-semibold text-foreground text-sm">
						{LL.CREATE_ROOM_FORM.MODEL_TITLE()}
					</p>
					<p className="text-muted-foreground text-xs leading-relaxed">
						{LL.CREATE_ROOM_FORM.MODEL_DESCRIPTION()}
					</p>
				</div>
			</div>

			<FormField
				control={form.control}
				name="model"
				render={({ field }) => (
					<ModelSelect
						aria-label={LL.CREATE_ROOM_FORM.MODEL_TITLE()}
						onValueChange={field.onChange}
						options={models}
						value={field.value}
					/>
				)}
			/>

			{selectedModel && (
				<p className="rounded-lg border border-border/60 bg-muted/20 px-3.5 py-3 text-muted-foreground text-xs leading-relaxed">
					{selectedModel.description}
				</p>
			)}

			<FormField
				control={form.control}
				name="sttDomain"
				render={({ field }) => (
					<MathDomainToggle
						domain={field.value}
						isSupported={selectedModel?.supportsDomain ?? false}
						onDomainChange={field.onChange}
					/>
				)}
			/>
			<div className="border-border/60 border-t pt-4">
				<FormField
					control={form.control}
					name="modelLocked"
					render={({ field }) => (
						<ToggleCard
							checked={field.value}
							description={LL.CREATE_ROOM_FORM.LOCK_MODEL_DESC()}
							icon={<Lock className="h-3.5 w-3.5" />}
							onCheckedChange={field.onChange}
							title={LL.CREATE_ROOM_FORM.LOCK_MODEL()}
						/>
					)}
				/>
			</div>
		</div>
	);
};
